import { useCallback, useEffect, useRef } from 'react'

import { segmentTrack,TrackType } from 'utils/tracking'

const SHORT_SESSION_DURATION = 1000 * 5
const MEDIUM_SESSION_DURATION = 1000 * 30
const LONG_SESSION_DURATION = 1000 * 60 * 5
const DWELL_TIME_TRACKING_INTERVAL = 3000

export interface SessionMetrics {
  sessionId: string
  countCardDwellTime: (insightId: number, timeInMs: number) => void
}

export const useSessionMetrics = (): SessionMetrics => {
  const sessionId = useRef<string>(
    Math.random()
      .toString()
      .substring(2)
  )
  const cardDwellTimes = useRef<Map<number, number>>(new Map())
  const bufferedCardIds = useRef<Set<number>>(new Set())

  const countCardDwellTime = useCallback((insightId: number, timeInMs: number) => {
    cardDwellTimes.current.set(insightId, (cardDwellTimes.current.get(insightId) || 0) + timeInMs)
    bufferedCardIds.current.add(insightId)
  }, [])

  const trackSession = useCallback((type: TrackType) => {
    segmentTrack(type, {
      lastPage: window.location.href,
      sessionId: sessionId.current
    })
  }, [])

  useEffect(() => {
    const timeout = setInterval(() => {
      if (bufferedCardIds.current.size > 0) {
        const cardViews = [...bufferedCardIds.current].map(cardId => ({
          insightId: cardId,
          dwellTime: cardDwellTimes.current.get(cardId)
        }))
        segmentTrack(TrackType.CCRM_FEED_CARD_DWELL_TIME, {
          sessionId: sessionId.current,
          cardViews,
          totalCardsViewed: cardDwellTimes.current.size
        })
        bufferedCardIds.current.clear()
      }
    }, DWELL_TIME_TRACKING_INTERVAL)
    return () => clearTimeout(timeout)
  })

  useEffect(() => {
    trackSession(TrackType.CCRM_SESSION_START)
    const shortTimeout = setTimeout(
      () => trackSession(TrackType.CCRM_SESSION_SHORT),
      SHORT_SESSION_DURATION
    )
    const mediumTimeout = setTimeout(
      () => trackSession(TrackType.CCRM_SESSION_MEDIUM),
      MEDIUM_SESSION_DURATION
    )
    const longTimeout = setTimeout(
      () => trackSession(TrackType.CCRM_SESSION_LONG),
      LONG_SESSION_DURATION
    )
    return () => {
      clearTimeout(shortTimeout)
      clearTimeout(mediumTimeout)
      clearTimeout(longTimeout)
    }
  }, [trackSession])

  return {
    sessionId: sessionId.current,
    countCardDwellTime
  }
}
